import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Brain, 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle,
  Play,
  Clock,
  BarChart3,
  Trash2,
  XCircle,
  Code,
  ChevronDown
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import { formatWithUTC } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { getFindingTypeLabel, getFindingTypeIcon, getFindingTypeColor } from '@/lib/modules/finding-type-labels';

interface AnalysisResult {
  id: string;
  type: string;
  status: 'completed' | 'pending' | 'failed';
  created_at: string;
  findings_count: number;
  confidence: number;
  summary: any;
}

interface Finding {
  id: string;
  finding_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  created_at: string;
  data_reference?: any;
}

interface ModuleAnalysesTabProps {
  auditId: string;
  moduleId: string;
  onStartAnalysis?: () => void;
}

const getSeverityIcon = (severity: 'low' | 'medium' | 'high' | 'critical') => {
  switch (severity) {
    case 'critical':
    case 'high':
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case 'medium':
      return <AlertTriangle className="h-4 w-4 text-warning" />;
    case 'low':
      return <CheckCircle className="h-4 w-4 text-success" />;
    default:
      return <CheckCircle className="h-4 w-4" />;
  }
};

const getSeverityColor = (severity: 'low' | 'medium' | 'high' | 'critical') => {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'medium':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'low':
      return 'bg-success/10 text-success border-success/20';
    default:
      return 'bg-muted/50';
  }
};

const getAnalysisTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'tax_account': 'Skattekontoanalys',
    'vat_analysis': 'Momsanalys',
    'payroll_analysis': 'Löneanalys',
    'revenue_analysis': 'Intäktsanalys',
    'cost_analysis': 'Kostnadsanalys'
  };
  return labels[type] || type;
};

/**
 * Normalize traceability data from various sources in a finding.
 * Prioritizes: top-level > data_reference > data_reference.metrics > affected_transactions
 */
interface AffectedTransaction {
  date?: string;
  amount?: number;
  row_index?: number;
  description?: string;
}

interface NormalizedTraceability {
  rowIndices: number[];
  positionNote: string | null;
  affectedTransactions: AffectedTransaction[];
  dates: string[];
  amounts: number[];
}

function normalizeTraceability(finding: Finding): NormalizedTraceability {
  const dr = finding.data_reference || {};
  
  // ✅ CANONICAL PRIORITY: First check top-level canonical fields, then fallback
  
  // 1. Extract row_indices - canonical first
  let rowIndices: number[] = [];
  if (Array.isArray(dr.row_indices) && dr.row_indices.length > 0) {
    // Priority 1: Canonical top-level row_indices
    rowIndices = dr.row_indices.filter((n: any) => typeof n === 'number');
  } else if (Array.isArray(dr.metrics?.row_indices) && dr.metrics.row_indices.length > 0) {
    // Priority 2: Legacy metrics.row_indices
    rowIndices = dr.metrics.row_indices.filter((n: any) => typeof n === 'number');
  } else if (Array.isArray(dr.affected_transactions)) {
    // Priority 3: Extract from affected_transactions
    rowIndices = dr.affected_transactions
      .map((t: AffectedTransaction) => t.row_index)
      .filter((n: any): n is number => typeof n === 'number');
  } else if (Array.isArray(dr.dataReferences?.rowNumbers)) {
    // Priority 4: Legacy dataReferences.rowNumbers
    rowIndices = dr.dataReferences.rowNumbers.filter((n: any) => typeof n === 'number');
  }
  
  // 2. Extract position_note - canonical first
  let positionNote: string | null = dr.position_note || null;
  if (!positionNote && rowIndices.length > 0) {
    positionNote = `Berör rad ${rowIndices.join(', ')}`;
  }
  
  // 3. Extract affected_transactions
  let affectedTransactions: AffectedTransaction[] = [];
  if (Array.isArray(dr.affected_transactions)) {
    affectedTransactions = dr.affected_transactions;
  }
  
  // 4. Extract dates - canonical first
  let dates: string[] = [];
  if (Array.isArray(dr.dates) && dr.dates.length > 0) {
    // Priority 1: Canonical top-level dates
    dates = dr.dates.filter((d: any) => typeof d === 'string');
  } else if (affectedTransactions.length > 0) {
    // Priority 2: Extract from affected_transactions
    dates = affectedTransactions
      .map((t: AffectedTransaction) => t.date)
      .filter((d: any): d is string => typeof d === 'string' && d.length > 0);
  } else if (Array.isArray(dr.dataReferences?.dates)) {
    // Priority 3: Legacy dataReferences.dates
    dates = dr.dataReferences.dates;
  }
  // Add specific date fields if not already present
  if (dr.paidDate && !dates.includes(dr.paidDate)) dates.push(dr.paidDate);
  if (dr.dueDate && !dates.includes(dr.dueDate)) dates.push(dr.dueDate);
  
  // 5. Extract amounts - canonical first
  let amounts: number[] = [];
  if (Array.isArray(dr.amounts) && dr.amounts.length > 0) {
    // Priority 1: Canonical top-level amounts
    amounts = dr.amounts.filter((a: any) => typeof a === 'number');
  } else if (affectedTransactions.length > 0) {
    // Priority 2: Extract from affected_transactions
    amounts = affectedTransactions
      .map((t: AffectedTransaction) => t.amount)
      .filter((a: any): a is number => typeof a === 'number');
  } else if (Array.isArray(dr.dataReferences?.amounts)) {
    // Priority 3: Legacy dataReferences.amounts
    amounts = dr.dataReferences.amounts;
  }
  // Add finding-level amount if not already present
  if (typeof dr.amount === 'number' && !amounts.includes(dr.amount)) {
    amounts.push(dr.amount);
  }
  
  return { rowIndices, positionNote, affectedTransactions, dates, amounts };
}

// Removed - using formatWithUTC from @/lib/datetime

export function ModuleAnalysesTab({ auditId, moduleId, onStartAnalysis }: ModuleAnalysesTabProps) {
  const { toast } = useToast();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [revisionHistory, setRevisionHistory] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [revisionToDelete, setRevisionToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasCompletedAnalysis, setHasCompletedAnalysis] = useState(false);
  const [stats, setStats] = useState<{
    queuedBatches: number;
    processingBatches: number;
    completedBatches: number;
    failedBatches: number;
    totalTokens: number;
    totalCost: number;
  } | null>(null);

  // ⭐ Track last known revision status to detect transitions (processing → completed)
  const lastKnownRevisionStatusRef = useRef<string | null>(null);

  useEffect(() => {
    console.log('[ModuleAnalysesTab] Component mounted/updated:', { auditId, moduleId });
    fetchAnalysisData();
  }, [auditId, moduleId]);

  // Add manual refresh function
  const refreshAnalysisData = () => {
    console.log('[ModuleAnalysesTab] Manual refresh triggered');
    fetchAnalysisData();
  };

  // Cancel active analysis
  const cancelActiveAnalysis = async () => {
    try {
      // Find active revisions for this module
      const { data: activeRevisions } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .in('status', ['processing', 'queued', 'pending']);

      if (activeRevisions && activeRevisions.length > 0) {
        const revisionIds = activeRevisions.map(r => r.id);
        
        // Cancel all batches for these revisions
        await supabase
          .from('batches')
          .update({
            state: 'failed',
            last_error: 'Cancelled by user',
            completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .in('revision_id', revisionIds)
          .in('state', ['queued', 'processing', 'retry', 'rate_limited']);

        // Cancel the revisions
        await supabase
          .from('revisions')
          .update({
            status: 'cancelled',
            last_error: 'Cancelled by user',
            completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .in('id', revisionIds);

        toast({
          title: 'Analys avbruten',
          description: `${revisionIds.length} analys(er) har avbrutits`,
        });

        // Refresh data
        fetchAnalysisData();
      } else {
        // No active revision found. In practice this often means the run already ended (cancelled/failed/completed)
        setStats(null);

        toast({
          title: 'Ingen aktiv analys',
          description: 'Analysen verkar redan vara avslutad. Statistik-kortet har dolts.',
        });

        fetchAnalysisData();
        fetchRevisionHistory();
      }
    } catch (error) {
      console.error('[cancelActiveAnalysis] Error:', error);
      toast({
        title: 'Fel',
        description: 'Kunde inte avbryta analysen',
        variant: 'destructive',
      });
    }
  };

  // Poll stats for real-time updates - DATABASE IS THE SINGLE SOURCE OF TRUTH
  const pollStats = useCallback(async () => {
    const previousStatus = lastKnownRevisionStatusRef.current;
    
    // 🔍 LOG: Start of pollStats
    console.log('[pollStats] Polling...', { 
      auditId, 
      moduleId, 
      previousStatus 
    });

    try {
      // 1️⃣ Hämta senaste revisionen för denna modul (oavsett status)
      const { data: latestRevisions, error: latestError } = await supabase
        .from('revisions')
        .select('id, total_tokens_in, total_tokens_out, total_cost_sek, status, created_at, updated_at, last_error')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (latestError) throw latestError;

      // 2️⃣ Om inga revisions finns, dölj stats och avsluta
      if (!latestRevisions || latestRevisions.length === 0) {
        console.log('[pollStats] No revisions found for this module');
        setStats(null);
        lastKnownRevisionStatusRef.current = null;
        return;
      }

      const latestRevision = latestRevisions[0];
      const currentStatus = latestRevision.status;

      // 3️⃣ ⭐ NYCKEL: Detektera statusövergång (processing → completed/failed/cancelled)
      const wasProcessing = previousStatus === 'processing';
      const isNowDone = ['completed', 'failed', 'cancelled'].includes(currentStatus);
      
      // 🔍 LOG: Revision state and transition detection
      console.log('[pollStats] Latest revision:', {
        id: latestRevision.id,
        status: currentStatus,
        previousStatus,
        updated_at: latestRevision.updated_at,
        wasProcessing,
        isNowDone,
        transitionDetected: wasProcessing && isNowDone
      });
      
      if (wasProcessing && isNowDone) {
        console.log('[pollStats] ⭐ Status transition detected:', previousStatus, '→', currentStatus);
        
        // Uppdatera findings och historik
        fetchAnalysisData();
        fetchRevisionHistory();
        
        if (currentStatus === 'completed') {
          toast({
            title: '✅ Analys klar',
            description: 'Resultaten har uppdaterats',
          });
        } else if (currentStatus === 'failed') {
          toast({
            title: '❌ Analys misslyckades',
            description: latestRevision.last_error || 'Ett fel uppstod',
            variant: 'destructive',
          });
        } else if (currentStatus === 'cancelled') {
          toast({
            title: 'Analys avbruten',
            description: 'Analysen har avbrutits',
          });
        }
      }

      // 4️⃣ Uppdatera senast kända status
      lastKnownRevisionStatusRef.current = currentStatus;

      // 5️⃣ ⭐ DATABAS-CENTRERAT: Visa stats ENDAST om revisionen fortfarande processar
      // ELLER om det finns queued/processing batches
      const isProcessing = currentStatus === 'processing';
      
      if (!isProcessing) {
        // Dubbelkolla om det finns aktiva batches (för edge cases)
        const { data: activeBatches } = await supabase
          .from('batches')
          .select('id')
          .eq('revision_id', latestRevision.id)
          .in('state', ['queued', 'processing'])
          .limit(1);
        
        const hasActiveBatches = activeBatches && activeBatches.length > 0;
        
        // 🔍 LOG: Stats decision when not processing
        console.log('[pollStats] Stats decision:', {
          showStats: false,
          reason: hasActiveBatches 
            ? 'Has active batches but revision not processing (edge case)' 
            : 'No active analysis - revision status: ' + currentStatus,
          revisionStatus: currentStatus,
          hasActiveBatches
        });
        
        if (!hasActiveBatches) {
          // Ingen pågående analys - dölj stats
          setStats(null);
          return;
        }
      }

      // 6️⃣ Hämta batch-statistik
      const { data: batches, error: batchesError } = await supabase
        .from('batches')
        .select('state')
        .eq('revision_id', latestRevision.id);

      if (batchesError) throw batchesError;

      const queuedBatches = batches?.filter(b => b.state === 'queued').length || 0;
      const processingBatches = batches?.filter(b => b.state === 'processing').length || 0;
      const completedBatches = batches?.filter(b => b.state === 'completed').length || 0;
      const failedBatches = batches?.filter(b => b.state === 'failed').length || 0;

      // 🔍 LOG: Batch stats
      console.log('[pollStats] Batch stats:', {
        revisionId: latestRevision.id,
        queued: queuedBatches,
        processing: processingBatches,
        completed: completedBatches,
        failed: failedBatches,
        hasActiveBatches: queuedBatches > 0 || processingBatches > 0
      });

      const totalTokens = (latestRevision.total_tokens_in || 0) + (latestRevision.total_tokens_out || 0);
      const totalCost = latestRevision.total_cost_sek || 0;

      // 🔍 LOG: Stats decision when processing
      console.log('[pollStats] Stats decision:', {
        showStats: true,
        reason: 'Analysis in progress',
        revisionStatus: currentStatus
      });

      // 7️⃣ Visa stats
      setStats({
        queuedBatches,
        processingBatches,
        completedBatches,
        failedBatches,
        totalTokens,
        totalCost,
      });
    } catch (err) {
      console.error('[pollStats] Error:', err);
    }
  }, [auditId, moduleId, toast]);

  // Fetch revision history for delete functionality
  const fetchRevisionHistory = async () => {
    const startTime = performance.now();
    
    // 🔍 LOG: Start of fetchRevisionHistory
    console.log('[fetchRevisionHistory] Starting...', { auditId, moduleId });
    
    try {
      const { data: revisions, error } = await supabase
        .from('revisions')
        .select('id, created_at, file_name, status')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .order('created_at', { ascending: false })

      const revisionsTime = performance.now() - startTime;
      
      // 🔍 LOG: Revisions query result
      console.log('[fetchRevisionHistory] Revisions fetched:', {
        count: revisions?.length || 0,
        statuses: revisions?.map(r => r.status) || [],
        queryTime: `${revisionsTime.toFixed(1)}ms`,
        error: error?.message || null
      });

      if (error) throw error

      // For each revision, fetch findings count and row count
      const revisionsWithStats = await Promise.all(
        (revisions || []).map(async (rev) => {
          const { data: aggregate } = await supabase
            .from('aggregates')
            .select('summary_json')
            .eq('revision_id', rev.id)
            .single()

          const findings = (aggregate?.summary_json as any)?.findings || []
          // Fallback: prioritize totalRows, fall back to data_summary.row_count
          const summaryJson = aggregate?.summary_json as any;
          const totalRows = summaryJson?.totalRows ?? summaryJson?.data_summary?.row_count ?? 0
          
          return {
            ...rev,
            totalRows,
            findingsCount: findings.length,
            criticalCount: findings.filter((f: any) => f.severity === 'critical').length,
            highCount: findings.filter((f: any) => f.severity === 'high').length
          }
        })
      )

      const totalTime = performance.now() - startTime;
      
      // 🔍 LOG: Final result
      console.log('[fetchRevisionHistory] ✅ Complete:', {
        revisionsCount: revisionsWithStats.length,
        totalFindings: revisionsWithStats.reduce((sum, r) => sum + r.findingsCount, 0),
        totalTime: `${totalTime.toFixed(1)}ms`
      });

      setRevisionHistory(revisionsWithStats)
    } catch (err) {
      const totalTime = performance.now() - startTime;
      console.error('[fetchRevisionHistory] ❌ Error:', {
        error: err instanceof Error ? err.message : err,
        totalTime: `${totalTime.toFixed(1)}ms`
      });
    }
  }

  const handleDeleteRevision = async () => {
    if (!revisionToDelete) return

    try {
      setIsDeleting(true)

      // 1. Delete audit_findings for this revision
      const { error: findingsError } = await supabase
        .from('audit_findings')
        .delete()
        .eq('run_id', revisionToDelete)

      if (findingsError) {
        console.warn('Warning: Could not delete audit_findings:', findingsError)
      }

      // 2. Delete worker_queue jobs for this revision
      const { error: queueError } = await supabase
        .from('worker_queue')
        .delete()
        .contains('payload', { revision_id: revisionToDelete })

      if (queueError) {
        console.warn('Warning: Could not delete worker_queue jobs:', queueError)
      }

      // 3. Delete batches
      const { error: batchesError } = await supabase
        .from('batches')
        .delete()
        .eq('revision_id', revisionToDelete)

      if (batchesError) throw batchesError

      // 4. Delete aggregates
      const { error: aggregatesError } = await supabase
        .from('aggregates')
        .delete()
        .eq('revision_id', revisionToDelete)

      if (aggregatesError) throw aggregatesError

      // 5. Delete revision_reports
      const { error: reportsError } = await supabase
        .from('revision_reports')
        .delete()
        .eq('revision_id', revisionToDelete)

      if (reportsError) {
        console.warn('Warning: Could not delete revision_reports:', reportsError)
      }

      // 6. Delete revision itself
      const { error: revisionError } = await supabase
        .from('revisions')
        .delete()
        .eq('id', revisionToDelete)

      if (revisionError) throw revisionError

      toast({
        title: "Revision raderad",
        description: "Revisionen och alla relaterade data har raderats",
      })

      // Update UI
      await fetchAnalysisData()
      await fetchRevisionHistory()
      setDeleteDialogOpen(false)
      setRevisionToDelete(null)
    } catch (err) {
      console.error('Error deleting revision:', err)
      toast({
        title: "Fel vid radering",
        description: err instanceof Error ? err.message : "Kunde inte radera revisionen",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Set up polling interval
  useEffect(() => {
    let interval: number | undefined;

    pollStats();
    interval = window.setInterval(pollStats, 2000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pollStats]);

  useEffect(() => {
    fetchAnalysisData();
    fetchRevisionHistory();
  }, [auditId, moduleId]);

  // Realtime subscription för automatisk uppdatering av findings
  // Lyssnar på BÅDE INSERT (ny analys) och UPDATE (statusändringar)
  useEffect(() => {
    const channel = supabase
      .channel(`module-analysis-${auditId}-${moduleId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'revisions',
          filter: `audit_id=eq.${auditId}`
        },
        (payload) => {
          const newRevision = payload.new as any;
          // 🔍 LOG: Realtime INSERT event
          console.log('[Realtime INSERT]', {
            revisionId: newRevision.id,
            moduleType: newRevision.module_type,
            matchesCurrentModule: newRevision.module_type === moduleId,
            status: newRevision.status,
            auditId: newRevision.audit_id
          });
          
          if (newRevision.module_type === moduleId) {
            // Reset tracking för ny analys
            lastKnownRevisionStatusRef.current = newRevision.status;
            fetchRevisionHistory();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'revisions',
          filter: `audit_id=eq.${auditId}`
        },
        (payload) => {
          const updatedRevision = payload.new as any;
          const oldRevision = payload.old as any;
          
          // 🔍 LOG: Realtime UPDATE event
          console.log('[Realtime UPDATE]', {
            revisionId: updatedRevision.id,
            moduleType: updatedRevision.module_type,
            matchesCurrentModule: updatedRevision.module_type === moduleId,
            oldStatus: oldRevision?.status,
            newStatus: updatedRevision.status,
            statusChanged: oldRevision?.status !== updatedRevision.status
          });
          
          if (updatedRevision.module_type === moduleId) {
            // Om analysen är klar, hämta data direkt (polling kommer också göra det, men detta är snabbare)
            if (['completed', 'failed', 'cancelled'].includes(updatedRevision.status)) {
              console.log('[Realtime UPDATE] ⭐ Analysis finished with status:', updatedRevision.status);
              fetchAnalysisData();
              fetchRevisionHistory();
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [auditId, moduleId]);

  /**
   * SINGLE SOURCE OF TRUTH: Fetch findings directly from audit_findings table
   * Only fetches findings for the LATEST COMPLETED revision to avoid orphan data
   */
  const fetchAnalysisData = async () => {
    const startTime = performance.now();
    
    // 🔍 LOG: Start of fetchAnalysisData
    console.log('[fetchAnalysisData] Starting... (SSOT: audit_findings for latest revision)', { auditId, moduleId });
    
    try {
      setLoading(true);
      
      // Get the LATEST completed revision for this module
      const { data: latestRevision, error: revisionsError } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', auditId)
        .eq('module_type', moduleId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const revisionsTime = performance.now() - startTime;
      
      console.log('[fetchAnalysisData] Latest revision check:', {
        revisionId: latestRevision?.id || null,
        hasCompleted: !!latestRevision,
        queryTime: `${revisionsTime.toFixed(1)}ms`,
        error: revisionsError?.message || null
      });

      if (revisionsError) throw revisionsError;

      if (!latestRevision) {
        console.log('[fetchAnalysisData] No completed revisions found - clearing findings');
        setFindings([]);
        setHasCompletedAnalysis(false);
        setLoading(false);
        return;
      }

      // SINGLE SOURCE OF TRUTH: Fetch findings ONLY for the latest revision
      const { data: auditFindingsData, error: findingsError } = await supabase
        .from('audit_findings')
        .select('*')
        .eq('run_id', latestRevision.id)
        .order('created_at', { ascending: false });

      const findingsTime = performance.now() - startTime;
      
      console.log('[fetchAnalysisData] Findings fetched for revision:', {
        revisionId: latestRevision.id,
        count: auditFindingsData?.length || 0,
        queryTime: `${findingsTime.toFixed(1)}ms`,
        error: findingsError?.message || null
      });

      if (findingsError) throw findingsError;

      if (auditFindingsData && auditFindingsData.length > 0) {
        // Map audit_findings to the format expected by the component
        const extractedFindings = auditFindingsData.map((f: any) => ({
          id: f.id,
          finding_type: f.finding_type || 'unknown',
          severity: f.severity || 'low',
          description: f.description || '',
          created_at: f.created_at,
          data_reference: f.data_reference || {}
        }));
        
        const totalTime = performance.now() - startTime;
        
        console.log('[fetchAnalysisData] ✅ Complete (SSOT):', {
          findingsCount: extractedFindings.length,
          revisionId: latestRevision.id,
          severities: extractedFindings.reduce((acc: Record<string, number>, f: any) => {
            acc[f.severity] = (acc[f.severity] || 0) + 1;
            return acc;
          }, {}),
          totalTime: `${totalTime.toFixed(1)}ms`
        });
        
        setFindings(extractedFindings);
        setHasCompletedAnalysis(true);
      } else {
        const totalTime = performance.now() - startTime;
        console.log('[fetchAnalysisData] No findings for latest revision', {
          revisionId: latestRevision.id,
          totalTime: `${totalTime.toFixed(1)}ms`
        });
        setFindings([]);
        setHasCompletedAnalysis(true); // Has completed revision but no findings
      }
      
    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error('[fetchAnalysisData] ❌ Error:', {
        error: error instanceof Error ? error.message : error,
        totalTime: `${totalTime.toFixed(1)}ms`
      });
      setFindings([]);
    } finally {
      setLoading(false);
    }
  };

  const startTaxAccountAnalysis = () => {
    if (onStartAnalysis) {
      onStartAnalysis();
    } else {
      toast({
        title: "Analys startad",
        description: "Skattekontoanalysen påbörjas i Skattekonto-fliken"
      });
    }
  };

  // Report generation functions removed - use ModuleReportTab instead

  const analysisStats = {
    total: findings.length,
    high: findings.filter(f => f.severity === 'high' || f.severity === 'critical').length,
    medium: findings.filter(f => f.severity === 'medium').length,
    low: findings.filter(f => f.severity === 'low').length,
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Analyser
          </div>
          <div className="flex items-center gap-2">
            {stats && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={cancelActiveAnalysis}
                className="gap-2"
              >
                <XCircle className="h-4 w-4" />
                Avbryt
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshAnalysisData}
              disabled={loading}
              className="gap-2"
            >
              <Clock className="h-4 w-4" />
              {loading ? "Laddar..." : "Uppdatera"}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          
          {/* Real-time Statistics */}
          {stats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 animate-pulse text-blue-600" />
                  Realtidsstatistik
                </CardTitle>
                <CardDescription>Live-uppdatering av analysprocessen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium">I kö</div>
                    <div className="text-2xl font-bold">{stats.queuedBatches}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Processar</div>
                    <div className="text-2xl font-bold">{stats.processingBatches}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Klara</div>
                    <div className="text-2xl font-bold text-green-600">{stats.completedBatches}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Fel</div>
                    <div className="text-2xl font-bold text-red-600">{stats.failedBatches}</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Totalt batchar:</span>
                    <span className="font-medium">
                      {stats.completedBatches} / {stats.queuedBatches + stats.processingBatches + stats.completedBatches + stats.failedBatches}
                    </span>
                  </div>
                  <Progress 
                    value={
                      ((stats.completedBatches / 
                        (stats.queuedBatches + stats.processingBatches + stats.completedBatches + stats.failedBatches || 1)) * 100)
                    } 
                  />
                </div>

                {stats.totalTokens > 0 && (
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Totala tokens:</span>
                      <span className="font-medium">{stats.totalTokens.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Revision History */}
          {revisionHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Analyshistorik ({revisionHistory.length})
                </CardTitle>
                <CardDescription>
                  Alla körda analyser för denna modul. Den senaste visas i översikten.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {revisionHistory.map((revision, index) => (
                    <Card key={revision.id} className={index === 0 ? 'border-primary/50 bg-primary/5' : ''}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {index === 0 && (
                                <Badge variant="default" className="text-xs">
                                  Senaste (visas i översikt)
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-xs">
                                {revision.status === 'completed' ? '✓ Klar' : 
                                 revision.status === 'processing' ? '⏳ Processar' : 
                                 revision.status === 'failed' ? '✗ Misslyckad' : revision.status}
                              </Badge>
                            </div>
                            
                            <div className="text-sm space-y-1">
                              <div className="font-medium">
                                {revision.file_name || 'Ingen fil'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatWithUTC(revision.created_at)}
                              </div>
                              <div className="flex gap-3 text-xs text-muted-foreground">
                                <span>{revision.totalRows || 0} rader</span>
                                <span>•</span>
                                <span>{revision.findingsCount} fynd</span>
                                {revision.criticalCount > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-destructive font-medium">
                                      {revision.criticalCount} kritiska
                                    </span>
                                  </>
                                )}
                                {revision.highCount > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-warning font-medium">
                                      {revision.highCount} höga
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setRevisionToDelete(revision.id)
                              setDeleteDialogOpen(true)
                            }}
                            title="Radera revision"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}


          {/* Analysis Summary */}
          {findings.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Totalt</p>
                    <p className="text-2xl font-bold">{analysisStats.total}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Hög risk</p>
                    <p className="text-2xl font-bold text-destructive">{analysisStats.high}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-warning/10 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Medel risk</p>
                    <p className="text-2xl font-bold text-warning">{analysisStats.medium}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-success/10 rounded-lg">
                    <CheckCircle className="h-4 w-4 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Låg risk</p>
                    <p className="text-2xl font-bold text-success">{analysisStats.low}</p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Module-specific Analysis Actions */}
          {moduleId === 'taxes_fees' && (
            <Card className="border-dashed">
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <div className="p-3 bg-primary/10 rounded-full w-fit mx-auto">
                    <Brain className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Skattekontoanalys</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Analysera uppladdade skattekontofiler för sena betalningar och avvikelser
                    </p>
                    {findings.length > 0 && (
                      <div className="p-3 mb-4 bg-warning/10 rounded-lg border border-warning/20">
                        <div className="flex items-center gap-2 text-sm text-warning-foreground">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          <strong>Observera:</strong> En ny analys kommer att ersätta alla befintliga analysresultat för denna modul.
                        </div>
                      </div>
                    )}
                    <Button onClick={startTaxAccountAnalysis} className="gap-2">
                      <Play className="h-4 w-4" />
                      Starta ny skattekontoanalys
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Findings List */}
          {findings.length > 0 ? (
            <div className="space-y-4">
              <h3 className="font-semibold">Identifierade avvikelser ({findings.length})</h3>
              {findings.map((finding) => (
                <Card key={finding.id} className="border-l-4 border-l-primary/20">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      <div className="mt-1">
                        {getSeverityIcon(finding.severity)}
                      </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className={getSeverityColor(finding.severity)}>
                              {finding.severity === 'critical' ? 'Kritisk' :
                               finding.severity === 'high' ? 'Hög' : 
                               finding.severity === 'medium' ? 'Medel' : 'Låg'} risk
                            </Badge>
                            {/* Hybrid v2: Visa källa för fynd */}
                            {(finding.data_reference?.source || (finding as any).source) && (
                              <Badge 
                                variant="outline" 
                                className={
                                  (finding.data_reference?.source || (finding as any).source) === 'regel' 
                                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                                    : 'bg-purple-50 text-purple-700 border-purple-200'
                                }
                              >
                                {(finding.data_reference?.source || (finding as any).source) === 'regel' 
                                  ? '📏 Regel' 
                                  : '🤖 AI-upptäckt'}
                              </Badge>
                            )}
                            <Badge variant="secondary" className={cn("text-xs gap-1 flex items-center", getFindingTypeColor(finding.finding_type))}>
                              {React.createElement(getFindingTypeIcon(finding.finding_type), { className: "h-3 w-3" })}
                              {getFindingTypeLabel(finding.finding_type)}
                            </Badge>
                            {finding.data_reference?.source_file && (
                              <Badge variant="outline" className="text-xs bg-muted">
                                📄 {finding.data_reference.source_file}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium mb-1">{finding.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatWithUTC(finding.created_at)}
                          </p>
                          
                          {/* Traceability Data - supports both new and legacy formats */}
                          {(() => {
                            const trace = normalizeTraceability(finding);
                            const hasTraceability = trace.rowIndices.length > 0 || 
                                                   trace.dates.length > 0 || 
                                                   trace.amounts.length > 0 ||
                                                   trace.affectedTransactions.length > 0 ||
                                                   finding.data_reference?.dataReferences;
                            
                            if (!hasTraceability) return null;
                            
                            return (
                              <div className="mt-4 pt-4 border-t space-y-2">
                                <div className="text-sm font-medium">📌 Referenser i rådata:</div>
                                
                                {/* Row numbers */}
                                {trace.rowIndices.length > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    <strong>Rader:</strong> {trace.rowIndices.join(', ')}
                                  </div>
                                )}
                                
                                {/* Dates */}
                                {trace.dates.length > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    <strong>Datum:</strong> {[...new Set(trace.dates)].join(', ')}
                                  </div>
                                )}
                                
                                {/* Amounts (if not showing affected transactions) */}
                                {trace.amounts.length > 0 && trace.affectedTransactions.length === 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    <strong>Belopp:</strong> {[...new Set(trace.amounts)].map(a => a.toLocaleString()).join(', ')} SEK
                                  </div>
                                )}
                                
                                {/* Affected Transactions for AI findings */}
                                {trace.affectedTransactions.length > 0 && (
                                  <div className="mt-2">
                                    <div className="text-xs font-medium text-muted-foreground mb-1">
                                      Berörda transaktioner ({trace.affectedTransactions.length}):
                                    </div>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {trace.affectedTransactions.slice(0, 5).map((t, idx) => (
                                        <div key={idx} className="text-xs bg-muted/50 px-2 py-1 rounded flex items-center gap-2">
                                          {t.date && <span className="font-medium">{t.date}</span>}
                                          {t.amount !== undefined && (
                                            <span className={t.amount < 0 ? 'text-destructive' : 'text-success'}>
                                              {t.amount.toLocaleString()} SEK
                                            </span>
                                          )}
                                          {t.row_index !== undefined && (
                                            <span className="text-muted-foreground">(rad {t.row_index})</span>
                                          )}
                                          {t.description && (
                                            <span className="text-muted-foreground truncate max-w-[200px]" title={t.description}>
                                              {t.description}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                      {trace.affectedTransactions.length > 5 && (
                                        <div className="text-xs text-muted-foreground italic">
                                          ... och {trace.affectedTransactions.length - 5} till
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Legacy: transactionTypes if present */}
                                {finding.data_reference?.dataReferences?.transactionTypes && (
                                  <div className="text-xs text-muted-foreground">
                                    <strong>Transaktioner:</strong> {finding.data_reference.dataReferences.transactionTypes.join(', ')}
                                  </div>
                                )}
                                
                                {/* Debug: Raw data_reference viewer */}
                                <Collapsible className="mt-2">
                                  <CollapsibleTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 text-muted-foreground hover:text-foreground">
                                      <Code className="h-3 w-3" />
                                      Visa rådata
                                      <ChevronDown className="h-3 w-3" />
                                    </Button>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <pre className="mt-2 p-2 bg-muted/50 rounded text-[10px] overflow-auto max-h-40 text-muted-foreground">
                                      {JSON.stringify(finding.data_reference, null, 2)}
                                    </pre>
                                  </CollapsibleContent>
                                </Collapsible>
                              </div>
                            );
                          })()}
                        </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : hasCompletedAnalysis ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
              <p className="text-lg font-medium mb-2 text-success">Inga avvikelser identifierade</p>
              <p className="text-sm text-muted-foreground">
                Analysen genomfördes utan att hitta några potentiella avvikelser eller riskområden.
                Detta är ett positivt resultat som indikerar god efterlevnad.
              </p>
              <div className="mt-4 p-4 bg-success/10 rounded-lg border border-success/20 max-w-md mx-auto">
                <p className="text-sm text-foreground">
                  ✓ Betalningar inom tolerans för förfallodatum<br/>
                  ✓ Belopp i linje med historiska mönster<br/>
                  ✓ Inga oregelbundna transaktioner upptäckta
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Inga analyser har körts än</p>
              <p className="text-sm">
                {moduleId === 'taxes_fees' 
                  ? 'Använd Skattekonto-fliken för att ladda upp dokument och starta analys'
                  : 'Ladda upp dokument för att kunna starta analyser'
                }
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Radera revision?</AlertDialogTitle>
          <AlertDialogDescription>
            Detta kommer att permanent radera denna revision och alla relaterade data:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Alla analysresultat (findings)</li>
              <li>Alla batchar och processdata</li>
              <li>Aggregerad statistik</li>
            </ul>
            <p className="mt-3 font-medium text-destructive">
              Denna åtgärd kan inte ångras.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            Avbryt
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteRevision}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Raderar..." : "Radera revision"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}